import { useCallback, useState } from "react";

import toast from "components/partials/toast/toast";

import type { RefineEmailContentFormValues } from "components/modals/refine-email-content-modal/refine-email-content-modal.types";

import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { TouchpointDTOBatchJobExecutionStatus } from "generated/schemas";

import {
  useSetTouchpointDetailCache,
  type TouchpointAttributes,
} from "hooks/api/use-touchpoints";

import { getErrorMessage } from "utilities";
import { markEmailWriterPollingStarted } from "utilities/email-writer-polling-session";

const touchpointVersionApi = getTouchpointVersion();

type UseRefineEmailContentParams = {
  clientId: string;
  campaignId: string;
  touchpointId: string;
  versionId: string;
  currentTouchpoint: TouchpointAttributes;
  onSuccess?: () => void;
};

const waitForNextPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

export const useRefineEmailContent = ({
  clientId,
  campaignId,
  touchpointId,
  versionId,
  currentTouchpoint,
  onSuccess,
}: UseRefineEmailContentParams) => {
  const setTouchpointDetailCache = useSetTouchpointDetailCache();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const refineEmail = useCallback(
    async (values: RefineEmailContentFormValues): Promise<boolean> => {
      if (!versionId) {
        toast.error({
          data: {
            title: "Failed to rewrite email",
            message: "Creative version is missing. Please refresh and try again.",
          },
        });
        return false;
      }

      setIsSubmitting(true);

      try {
        await touchpointVersionApi.transformEmailAsync(
          { clientId, campaignId, touchpointId, versionId },
          {
            emailPrompt: values.emailAdditionalInstructions?.trim() || undefined,
          },
        );

        // Session must be active before RUNNING hits the cache, otherwise the badge
        // briefly renders as Monday "Syncing..." instead of "Writing...".
        // Scope to this touchpoint so completion polling hits detail only (not the list).
        markEmailWriterPollingStarted(campaignId, clientId, { touchpointId });

        const runningTouchpoint: TouchpointAttributes = {
          ...currentTouchpoint,
          id: touchpointId,
          batchJobExecutionStatus: TouchpointDTOBatchJobExecutionStatus.RUNNING,
        };

        setTouchpointDetailCache({ clientId, campaignId }, runningTouchpoint, {
          invalidateLists: false,
        });

        // Keep the modal up until Writing badge + preview loading can paint.
        await waitForNextPaint();

        toast.info({
          data: {
            title: "Email rewrite in progress",
            message:
              "Rewritten email content will appear in creative details, and a notification will be sent upon completion.",
          },
        });

        onSuccess?.();
        return true;
      } catch (error) {
        toast.error({
          data: {
            title: "Failed to rewrite email",
            message: getErrorMessage(error) || "Please try again.",
          },
        });
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      campaignId,
      clientId,
      currentTouchpoint,
      onSuccess,
      setTouchpointDetailCache,
      touchpointId,
      versionId,
    ],
  );

  return { refineEmail, isSubmitting };
};
